import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInAgency, signOutAdmin } from '../lib/auth';
import { useToast } from '../shared/components/ToastProvider';
import { activePb } from '../lib/pb';

export default function Login() {
  const { notify } = useToast();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  // Admin OTP magic link feature
  const [linkMode, setLinkMode] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const authData = await signInAgency(email.trim(), password);
      // Agency sign-in only. Engineers and clients have their own portals;
      // storing their session in the admin store leaves every admin route
      // bouncing back to this page (the "reverts to /#/admin/" loop).
      if (authData?.record?.role !== 'agency_admin' && authData?.record?.role !== 'agency_staff') {
        signOutAdmin();
        notify(authData?.record?.role === 'engineer'
          ? 'Engineer account — use the Engineer Portal sign-in'
          : 'This account cannot access the admin console', 'error');
        return;
      }
      console.log('Admin auth successful:', authData?.record?.email, 'Role:', authData?.record?.role);
      notify('Welcome back');
      navigate('/admin', { replace: true });
    } catch (err) {
      console.error('Admin login failed:', err);
      console.error('Error details:', err?.data?.message || err?.message);
      notify('Invalid email or password', 'error');
    } finally {
      setBusy(false);
    }
  }

  // Admin OTP magic link - send sign-in link for admin accounts
  async function sendAdminLink(event) {
    event.preventDefault();
    if (!linkEmail.trim() || linkBusy) return;
    setLinkBusy(true);
    try {
      // Request OTP for admin accounts
      await activePb().collection('accounts').requestOTP(linkEmail.trim());
      setLinkSent(true);
      notify('Sign-in link sent! Check your email (valid 10 minutes)');
    } catch (err) {
      // Treat as sent anyway to prevent email enumeration
      setLinkSent(true);
      notify('If an admin account exists, a sign-in link was sent to your email (valid 10 minutes)');
    } finally {
      setLinkBusy(false);
    }
  }

  return (
    <div className="standalone-page login-page">
      <section className="panel settings-form login-card">
        <div className="brand admin-brand login-brand">
          <div className="brand-mark">
            <span>PP</span>
          </div>
        </div>
        <h1>Premiere Portal</h1>
        <p className="eyebrow">Agency sign in</p>

        {!linkSent ? (
          <>
            {!linkMode && (
              <form onSubmit={submit}>
                <label>Email<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" /></label>
                <label>Password<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" /></label>
                <button className="primary-button" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
              </form>
            )}

            {linkMode && (
              <form onSubmit={sendAdminLink}>
                <label>Email
                  <input
                    type="email"
                    value={linkEmail}
                    onChange={(e) => setLinkEmail(e.target.value)}
                    placeholder="simon.bushell@premiereplumbing.co.uk"
                    required
                    autoComplete="username"
                  />
                </label>
                <button className="primary-button" disabled={linkBusy}>
                  {linkBusy ? 'Sending…' : 'Send me a sign-in link'}
                </button>
              </form>
            )}

            <button
              type="button"
              className="link-button"
              onClick={() => setLinkMode((v) => !v)}
            >
              {linkMode ? 'Sign in with password instead' : 'No password? Email me a sign-in link'}
            </button>
            <p className="login-hint">Admin sign-in links expire after 10 minutes and work for agency_admin and agency_staff accounts.</p>
          </>
        ) : (
          <div className="send-ok">
            <p>If an admin account exists for that email, a sign-in link is on its way — check your inbox (valid for 10 minutes).</p>
            <button className="primary-button" onClick={() => { setLinkSent(false); setLinkMode(false); }}>
              Back to sign in
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
